import { useEffect, useState } from "react";
import { Loader2, Pencil, Printer } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { TapDisabledReason } from "@/components/ui/TapDisabledReason";
import { quantityUnitSuffix } from "@/lib/mrpSkuUnitDisplay";
import { cn } from "@/lib/utils";

import { formatNumber, type InboundReceiptSubcontainer } from "./inboundApi";
import {
  formatInboundSubcontainerHeading,
  formatOpenSubcontainerHeading,
  parseInboundSubcontainerUnitKind,
  type InboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";
import { InboundSubcontainerUnitKindTiles } from "./InboundSubcontainerUnitKindTiles";
import type { OpenSubcontainerLine } from "./inboundReceiveSubcontainer";
import {
  inboundReceiveReprintTapReason,
  inboundReceiveSealTapReason,
  inboundReceiveUnsealTapReason,
} from "./inboundReceiveDisabledTap";

function sharedQuantityUnitSuffix(units: ReadonlyArray<string | null | undefined>): string {
  const suffixes = units.map((unit) => quantityUnitSuffix(unit));
  const first = suffixes[0];
  if (first == null || suffixes.some((suffix) => suffix !== first)) return "units";
  return first;
}

function quantityColumnWidth(lines: readonly { quantity: number }[]): string {
  const maxDigits = lines.reduce((max, line) => {
    const digits = formatNumber(line.quantity).length;
    return digits > max ? digits : max;
  }, 1);
  return `${String(maxDigits)}ch`;
}

function ContainerTypePicker(props: {
  value: InboundArrivalUnitKind | null;
  onChange: (kind: InboundArrivalUnitKind) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1 border-t border-slate-100 pt-2">
      <p className="text-[11px] font-medium text-slate-600">Container type</p>
      <InboundSubcontainerUnitKindTiles
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
      />
    </div>
  );
}

function ContainerTypeEditButton(props: {
  expanded: boolean;
  disabled: boolean;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="secondary"
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center p-0"
      disabled={props.disabled}
      aria-expanded={props.expanded}
      aria-label={props.label}
      onClick={props.onClick}
    >
      <Pencil className="h-4 w-4 shrink-0" aria-hidden />
    </Button>
  );
}

export function InboundReceiveSubcontainersPanel({
  subcontainers,
  openLines,
  nextContainerNo,
  openUnitKind,
  openUnitKindUserSet,
  onOpenUnitKindChange,
  onSealedUnitKindChange,
  sealedUnitKindPendingId,
  onUnseal,
  unsealPendingId,
  closePending,
  printPendingNo,
  onCloseAndPrint,
  onReprint,
  onEditLine,
  sealReady,
  sealBlockReason,
  labelPrinterSelected,
  unitOfMeasureBySourceItemId = {},
  readOnly = false,
}: {
  subcontainers: readonly InboundReceiptSubcontainer[];
  openLines: readonly OpenSubcontainerLine[];
  nextContainerNo: number;
  openUnitKind: InboundArrivalUnitKind | null;
  openUnitKindUserSet: boolean;
  onOpenUnitKindChange: (kind: InboundArrivalUnitKind) => void;
  onSealedUnitKindChange: (subcontainerId: string, kind: InboundArrivalUnitKind) => void;
  sealedUnitKindPendingId: string | null;
  onUnseal: (subcontainerId: string) => void;
  unsealPendingId: string | null;
  closePending: boolean;
  printPendingNo: number | null;
  onCloseAndPrint: () => void;
  onReprint: (subcontainerNo: number, items: InboundReceiptSubcontainer["items"]) => void;
  onEditLine: (sourceItemId: string) => void;
  sealReady: boolean;
  sealBlockReason: string | null;
  labelPrinterSelected: boolean;
  unitOfMeasureBySourceItemId?: Readonly<Record<string, string | null | undefined>>;
  readOnly?: boolean;
}): JSX.Element {
  const [expandedSealedIds, setExpandedSealedIds] = useState<Record<string, boolean>>({});
  const [openKindPickerExpanded, setOpenKindPickerExpanded] = useState(false);
  const [sealedKindEditId, setSealedKindEditId] = useState<string | null>(null);
  const sorted = [...subcontainers].sort((left, right) => left.subcontainer_no - right.subcontainer_no);
  const openQuantityWidth = quantityColumnWidth(openLines);
  const openHeading = formatOpenSubcontainerHeading(nextContainerNo, openUnitKind);
  const openTypePickerVisible =
    openLines.length > 0 && (!openUnitKindUserSet || openKindPickerExpanded);
  const controlsDisabled =
    closePending || printPendingNo != null || unsealPendingId != null;
  const sealDisabled = controlsDisabled || !sealReady;
  const sealTapReason = inboundReceiveSealTapReason({
    controlsBusy: controlsDisabled,
    sealReady,
    sealBlockReason,
  });

  useEffect(() => {
    if (openLines.length === 0) {
      setOpenKindPickerExpanded(false);
    }
  }, [openLines.length]);

  useEffect(() => {
    if (!readOnly) return;
    setExpandedSealedIds((previous) => {
      const next = { ...previous };
      for (const subcontainer of subcontainers) {
        next[subcontainer.id] = true;
      }
      return next;
    });
  }, [readOnly, subcontainers]);

  const handleOpenUnitKindPick = (kind: InboundArrivalUnitKind): void => {
    onOpenUnitKindChange(kind);
    setOpenKindPickerExpanded(false);
  };

  const handleSealedUnitKindPick = (subcontainerId: string, kind: InboundArrivalUnitKind): void => {
    onSealedUnitKindChange(subcontainerId, kind);
    setSealedKindEditId(null);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-y-scroll overscroll-y-contain py-1 [touch-action:pan-y] [-webkit-overflow-scrolling:touch]">
      {sorted.length === 0 && openLines.length === 0 ? (
        <p className="py-2 text-xs text-slate-500">
          {readOnly
            ? "No sealed containers on this finished crate."
            : "Count a line to start a container, then pick its type before sealing."}
        </p>
      ) : null}

      {!readOnly && openLines.length > 0 ? (
        <div className="shrink-0 overflow-hidden rounded-lg border border-brand-300 bg-white">
          <div className="space-y-2 border-b border-slate-100 px-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900">{openHeading}</h3>
              {openUnitKindUserSet ? (
                <ContainerTypeEditButton
                  expanded={openKindPickerExpanded}
                  disabled={controlsDisabled}
                  label={`Change type for ${openHeading}`}
                  onClick={() => setOpenKindPickerExpanded((visible) => !visible)}
                />
              ) : null}
              <span
                className={cn(
                  "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                  "bg-brand-50 text-brand-700 ring-brand-200",
                )}
              >
                Open
              </span>
              <div className="ml-auto flex shrink-0">
                <TapDisabledReason reason={sealTapReason}>
                  <Button
                    type="button"
                    variant="primary"
                    className="min-h-10 px-3 text-sm font-semibold"
                    disabled={sealDisabled}
                    onClick={onCloseAndPrint}
                  >
                    {closePending ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> : null}
                    Seal & print
                  </Button>
                </TapDisabledReason>
              </div>
            </div>
            {openTypePickerVisible ? (
              <ContainerTypePicker
                value={openUnitKind}
                onChange={handleOpenUnitKindPick}
                disabled={controlsDisabled}
              />
            ) : null}
            {sealBlockReason != null && openLines.length > 0 ? (
              <p className="text-[11px] text-slate-600" role="status">
                {sealBlockReason}
              </p>
            ) : null}
          </div>
          <ul className="space-y-1 border-l-2 border-brand-100 py-1 pl-2">
            {openLines.map((line) => (
              <li
                key={line.source_item_id}
                className="flex items-center justify-between gap-2 px-2 py-1 text-xs"
              >
                <span className="truncate font-mono font-semibold text-slate-900">{line.sku}</span>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className="tabular-nums text-slate-700 text-right"
                    style={{ minWidth: openQuantityWidth }}
                  >
                    {formatNumber(line.quantity)} {quantityUnitSuffix(unitOfMeasureBySourceItemId[line.source_item_id])}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    className="inline-flex min-h-10 items-center gap-1.5 px-3 text-sm font-semibold"
                    disabled={controlsDisabled}
                    onClick={() => onEditLine(line.source_item_id)}
                  >
                    <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                    Edit
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sorted.map((subcontainer) => {
        const unitKind = parseInboundSubcontainerUnitKind(subcontainer.unit_kind);
        const heading = formatInboundSubcontainerHeading(unitKind, subcontainer.subcontainer_no);
        const totalQty = subcontainer.items.reduce(
          (sum, item) => sum + (Number.parseFloat(item.quantity) || 0),
          0,
        );
        const sealedContentsExpanded = expandedSealedIds[subcontainer.id] === true;
        const sealedKindPickerVisible = sealedKindEditId === subcontainer.id;
        const sealedKindPending = sealedUnitKindPendingId === subcontainer.id;
        const unsealPending = unsealPendingId === subcontainer.id;
        const sealedQuantityWidth = quantityColumnWidth(
          subcontainer.items.map((item) => ({
            quantity: Number.parseFloat(item.quantity) || 0,
          })),
        );
        return (
          <div
            key={subcontainer.id}
            className="shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
          >
            <div className="space-y-2 border-b border-slate-100 px-2 py-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-900">{heading}</h3>
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                    "bg-slate-100 text-slate-600 ring-slate-200",
                  )}
                >
                  Sealed
                </span>
                <span className="text-[11px] text-slate-500">
                  {formatNumber(totalQty)}{" "}
                  {sharedQuantityUnitSuffix(
                    subcontainer.items.map((item) => unitOfMeasureBySourceItemId[item.source_item_id]),
                  )}
                </span>
                {!readOnly ? (
                  <ContainerTypeEditButton
                    expanded={sealedKindPickerVisible}
                    disabled={controlsDisabled || sealedKindPending}
                    label={`Change type for ${heading}`}
                    onClick={() =>
                      setSealedKindEditId((current) => (current === subcontainer.id ? null : subcontainer.id))
                    }
                  />
                ) : null}
                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  <TapDisabledReason
                    reason={inboundReceiveReprintTapReason({
                      busy: printPendingNo != null || closePending || sealedKindPending,
                      labelPrinterSelected,
                    })}
                  >
                  <Button
                    type="button"
                    variant="secondary"
                    className="inline-flex min-h-10 items-center gap-1.5 px-3 text-sm font-semibold"
                    disabled={printPendingNo != null || closePending || sealedKindPending || !labelPrinterSelected}
                    onClick={() => onReprint(subcontainer.subcontainer_no, subcontainer.items)}
                  >
                    {printPendingNo === subcontainer.subcontainer_no ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    Reprint
                  </Button>
                  </TapDisabledReason>
                  {!readOnly ? (
                    <TapDisabledReason
                      reason={inboundReceiveUnsealTapReason({
                        busy: controlsDisabled || sealedKindPending || unsealPending,
                        itemCount: subcontainer.items.length,
                      })}
                    >
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-10 px-3 text-sm font-semibold"
                        disabled={
                          controlsDisabled
                          || sealedKindPending
                          || unsealPending
                          || subcontainer.items.length === 0
                        }
                        title="Reopen this container so you can edit or remove lines before sealing again."
                        onClick={() => onUnseal(subcontainer.id)}
                      >
                        {unsealPending ? (
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        ) : null}
                        Unseal
                      </Button>
                    </TapDisabledReason>
                  ) : null}
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-10 px-3 text-sm font-semibold"
                    disabled={subcontainer.items.length === 0}
                    aria-expanded={sealedContentsExpanded}
                    onClick={() =>
                      setExpandedSealedIds((previous) => ({
                        ...previous,
                        [subcontainer.id]: !sealedContentsExpanded,
                      }))
                    }
                  >
                    {sealedContentsExpanded ? "Hide contents" : "View contents"}
                  </Button>
                </div>
              </div>
              {!readOnly && sealedKindPickerVisible ? (
                <ContainerTypePicker
                  value={unitKind}
                  onChange={(kind) => handleSealedUnitKindPick(subcontainer.id, kind)}
                  disabled={controlsDisabled || sealedKindPending}
                />
              ) : null}
            </div>
            {sealedContentsExpanded ? (
              <ul className="space-y-1 border-l-2 border-slate-200 py-1 pl-2">
                {subcontainer.items.length === 0 ? (
                  <li className="px-2 py-2 text-xs text-slate-500">No lines on this container yet.</li>
                ) : (
                  subcontainer.items.map((item) => (
                    <li
                      key={`${subcontainer.id}-${item.source_item_id}`}
                      className="flex items-center justify-between gap-2 px-2 py-1 text-xs"
                    >
                      <span className="truncate font-mono font-semibold text-slate-900">{item.sku}</span>
                      <span
                        className="shrink-0 tabular-nums text-slate-700 text-right"
                        style={{ minWidth: sealedQuantityWidth }}
                      >
                        {formatNumber(item.quantity)} {quantityUnitSuffix(unitOfMeasureBySourceItemId[item.source_item_id])}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
